import { LeadStatus } from '@up2stay/up2promo-api-client';

/** Board / filter order — pipeline only (archive is `archivedAt`, not a status). */
export const LEAD_STATUS_IDS = [
  LeadStatus.NEW,
  LeadStatus.CONTACTED,
  LeadStatus.QUALIFIED,
  LeadStatus.WON,
  LeadStatus.LOST,
] as const satisfies readonly LeadStatus[];

export type LeadStatusId = (typeof LEAD_STATUS_IDS)[number];

const LEAD_STATUS_VALUES = new Set<string>(Object.values(LeadStatus));

export function isLeadStatus(value: string): value is LeadStatus {
  return LEAD_STATUS_VALUES.has(value);
}

/**
 * Solid accent for column dots, minimap, and badge indicators.
 * Prefer design tokens for brand / ok / Signal Red; mid-pipeline hues stay
 * domain-semantic (not page-local brand washes).
 */
export const LEAD_STATUS_ACCENT: Record<LeadStatus, string> = {
  [LeadStatus.NEW]: 'var(--brand-500)',
  [LeadStatus.CONTACTED]: '#eab308',
  [LeadStatus.QUALIFIED]: '#22c55e',
  [LeadStatus.WON]: 'var(--ok)',
  [LeadStatus.LOST]: 'var(--destructive)',
};

/** Tinted badge chips — same hue family as {@link LEAD_STATUS_ACCENT}. */
export const LEAD_STATUS_BADGE_CLASSES: Record<LeadStatus, string> = {
  [LeadStatus.NEW]:
    'bg-[color:var(--ghost-accent-bg)] text-[color:var(--ghost-accent-fg)]',
  [LeadStatus.CONTACTED]: 'bg-amber-500/10 text-amber-800 dark:text-amber-300',
  [LeadStatus.QUALIFIED]: 'bg-green-500/10 text-green-800 dark:text-green-400',
  [LeadStatus.WON]: 'bg-[color:var(--ok-bg)] text-[color:var(--ok-fg)]',
  [LeadStatus.LOST]: 'bg-destructive/10 text-destructive',
};

export function leadStatusAccent(status: string): string {
  return isLeadStatus(status)
    ? LEAD_STATUS_ACCENT[status]
    : 'var(--muted-foreground)';
}

export function leadStatusBadgeClass(status: string): string {
  return isLeadStatus(status)
    ? LEAD_STATUS_BADGE_CLASSES[status]
    : 'bg-muted text-muted-foreground';
}
